// Single original-video picker with cancellable upload, policy guidance, and lazy preview.
// Replacement failures retain the selected video; removal never deletes shared assets.
import React, { useEffect, useId, useRef, useState } from "react";
import { getPromotionVideoConstraints } from "../constants/promotion_video";
import { uploadPromotionVideo } from "../services/promotion_video_upload_service";

export default function PromotionVideoUpload({ eventId, platform, postType, video, onChange, onBusyChange, onError, disabled }) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(false);
  const controller = useRef(null);
  const callbacks = useRef({ onBusyChange });
  callbacks.current = { onBusyChange };
  useEffect(() => () => {
    controller.current?.abort();
    callbacks.current.onBusyChange?.(false);
  }, []);
  useEffect(() => { setPreview(false); }, [video?.url]);
  const constraints = getPromotionVideoConstraints(platform, postType);
  if (!constraints) return null;

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || disabled || controller.current) return;
    const active = new AbortController();
    controller.current = active;
    setBusy(true);
    setProgress(0);
    onBusyChange?.(true);
    try {
      const result = await uploadPromotionVideo(file, eventId, { constraints, signal: active.signal, onProgress: (value) => { if (!active.signal.aborted) setProgress(value); } });
      if (!active.signal.aborted) { setPreview(false); onChange(result); }
    } catch (error) {
      if (!active.signal.aborted) onError?.(`Could not upload ${file.name}: ${error.message || "Upload failed."} Your previous selection is unchanged.`);
    } finally {
      if (controller.current === active) controller.current = null;
      if (!active.signal.aborted) { setBusy(false); onBusyChange?.(false); }
    }
  };
  const cancel = () => {
    controller.current?.abort();
    controller.current = null;
    setBusy(false);
    onBusyChange?.(false);
  };
  return <div className="mb-3">
    <label htmlFor={id} className="form-label">Promotion Video</label>
    <input id={id} className="form-control" type="file" accept="video/mp4,.mp4" disabled={disabled || busy} onChange={upload} />
    <div className="form-text">LGV draft limits: one MP4, up to 100 MiB, 5–60 seconds, up to 4096 pixels per side. These are LGV limits, not platform API maximums.</div>
    {constraints.recommendedWidth && <div className="form-text">Recommended for this Post Type: {constraints.recommendedWidth} × {constraints.recommendedHeight} pixels. Uploads stay unchanged; LGV does not resize or transcode videos.</div>}
    <div className="form-text">H.264 video with AAC audio is recommended. Browser metadata checks do not certify codecs. Uploaded assets are publicly accessible; use public promotional content only.</div>
    <div className="form-text">Video uploads are saved as draft assets. Direct video publishing is not yet available. <a href={constraints.helpUrl} target="_blank" rel="noopener noreferrer">Platform video API details</a></div>
    {busy && <div className="mt-2"><div role="status">Checking / uploading video: {progress}%</div><progress aria-label="Video upload progress" value={progress} max="100" /><button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={cancel}>Cancel upload</button></div>}
    {video ? <div className="border rounded p-2 mt-2">
      <div>{video.fileName || "Selected video"}</div>
      <div className="small text-muted">{Number(video.durationSeconds).toFixed(1)} seconds · {video.width} × {video.height} pixels · {(Number(video.sizeBytes) / 1024 / 1024).toFixed(1)} MiB</div>
      <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={() => setPreview((value) => !value)}>{preview ? "Close video preview" : "Preview video"}</button>
      <button type="button" className="btn btn-sm btn-outline-danger mt-2 ms-2" disabled={disabled || busy} onClick={() => { setPreview(false); onChange(null); }}>Remove video</button>
      {preview && <video className="d-block mt-2" aria-label="Selected promotion video" controls preload="none" src={video.url} style={{ maxWidth: "100%", maxHeight: 480 }} />}
    </div> : <div className="small text-muted mt-2">No promotion video selected.</div>}
  </div>;
}
